import { useState, useEffect } from 'react';
import { Container, Card, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getMyWallets, createWithdrawal, sendWithdrawalOTP, verifyAndProcessWithdrawal, getMyWithdrawals, cancelWithdrawal, getWithdrawalLimit, getWithdrawalFees } from '../../api/walletApi';
import { getMe } from '../../api/authApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.06) 0%, #0d0d0d 65%)',
  border: '1px solid rgba(0,229,255,0.15)',
  color: '#fff',
};

const inputStyle = {
  width: '100%',
  background: 'rgba(0,229,255,0.04)',
  border: '1px solid rgba(0,229,255,0.2)',
  borderRadius: 8,
  padding: '10px 14px',
  color: '#fff',
  fontSize: 14,
  outline: 'none',
};

const labelStyle = {
  color: '#888',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  display: 'block',
  marginBottom: 6,
};

const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email || '';
  const [local, domain] = email.split('@');
  const domainParts = domain.split('.');
  const ext = domainParts.length > 1 ? domainParts.pop() : '';
  return `${local.slice(0, 4)}**@${ext ? `*.${ext}` : '*'}`;
};

const statusColors = {
  pending:    { color: '#ffaa44', bg: 'rgba(255,170,68,0.12)', border: 'rgba(255,170,68,0.3)' },
  processing: { color: '#66aaff', bg: 'rgba(102,170,255,0.12)', border: 'rgba(102,170,255,0.3)' },
  cancelled:  { color: '#aaa',    bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
  rejected:   { color: '#ff6666', bg: 'rgba(200,50,50,0.12)', border: 'rgba(200,50,50,0.3)' },
  approved:   { color: '#66aaff', bg: 'rgba(102,170,255,0.12)', border: 'rgba(102,170,255,0.3)' },
  successful: { color: '#66ff99', bg: 'rgba(102,255,153,0.12)', border: 'rgba(102,255,153,0.3)' },
};

const StatusBadge = ({ status }) => {
  const s = statusColors[status] || statusColors.pending;
  return (
    <span style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
      {status}
    </span>
  );
};

const Withdraw = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [limitData, setLimitData] = useState(null);
  const [feeData, setFeeData] = useState({ externalFeePercent: 10, oneWalletFeePercent: 10 });
  const [profileUsdtAddress, setProfileUsdtAddress] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [destination, setDestination] = useState('');
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // OTP Verification
  const [otpStep, setOtpStep] = useState('form'); // 'form' or 'otp'
  const [otp, setOtp] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpSending, setOtpSending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [canResendOtp, setCanResendOtp] = useState(false);

  const feePercent = destination === 'external' ? feeData.externalFeePercent : feeData.oneWalletFeePercent;
  const numAmount = parseFloat(amount) || 0;
  const charges = parseFloat((numAmount * feePercent / 100).toFixed(2));
  const receivable = parseFloat((numAmount - charges).toFixed(2));
  const isBelowMin = numAmount > 0 && numAmount < 10;
  const isInsufficient = numAmount > 0 && balance !== null && numAmount > balance;
  const isOverLimit = numAmount > 0 && limitData !== null && numAmount > limitData.availableLimit;

  const fetchHistory = () =>
    getMyWithdrawals()
      .then((res) => setHistory(res.data.withdrawals))
      .finally(() => setLoadingHistory(false));

  const fetchLimit = () =>
    getWithdrawalLimit().then((res) => setLimitData(res.data)).catch(() => {});

  // OTP timer
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (otpTimer === 0 && otpStep === 'otp') setCanResendOtp(true);
  }, [otpTimer, otpStep]);

  useEffect(() => {
    getMyWallets().then((res) => setBalance(res.data.wallets.earning_wallet?.balance ?? 0));
    getMe().then((res) => {
      setProfileUsdtAddress(res.data.user?.usdtWalletAddress || '');
      setUserEmail(res.data.user?.email || '');
    });
    getWithdrawalFees().then((res) => setFeeData(res.data)).catch(() => {});
    fetchHistory();
    fetchLimit();
  }, []);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    if (!destination) return setError('Please select a withdrawal destination.');
    if (destination === 'external' && !profileUsdtAddress) return setError('No USDT wallet address found. Please update it in My Profile.');
    if (isBelowMin) return setError('Minimum withdrawal amount is 10 USDT.');
    if (isInsufficient) return setError('Insufficient balance.');
    if (isOverLimit) return setError('Exceeds your withdrawal limit.');

    setOtpSending(true);
    setOtpError('');
    try {
      await sendWithdrawalOTP({ destination, amount: numAmount, externalAddress: profileUsdtAddress });
      setOtpStep('otp');
      setOtpTimer(30);
      setCanResendOtp(false);
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setOtpSending(false);
    }
  };

  const handleResendOTP = async () => {
    setOtpSending(true);
    setOtpError('');
    try {
      await sendWithdrawalOTP({ destination, amount: numAmount, externalAddress: profileUsdtAddress });
      setOtpTimer(30);
      setCanResendOtp(false);
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyAndWithdraw = async (e) => {
    e.preventDefault();
    setError('');
    setOtpError('');
    if (!otp.trim() || otp.length !== 6) return setOtpError('Please enter a valid 6-digit OTP');

    setSubmitting(true);
    try {
      const res = await verifyAndProcessWithdrawal({
        amount: numAmount,
        destination,
        externalAddress: profileUsdtAddress,
        otp
      });
      setSuccessData(res.data.withdrawal);
      setAmount('');
      setDestination('');
      setOtp('');
      setOtpStep('form');
      setBalance((prev) => parseFloat((prev - numAmount).toFixed(2)));
      fetchHistory();
      fetchLimit();
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Withdrawal failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    setCancellingId(id);
    try {
      await cancelWithdrawal(id);
      await getMyWallets().then((res) => setBalance(res.data.wallets.earning_wallet?.balance ?? 0));
      fetchHistory();
      fetchLimit();
    } catch (err) {
      setError(err.response?.data?.message || 'Cancel failed.');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4" style={{ maxWidth: 560 }}>
        <button onClick={() => navigate('/wallet?type=earning')} style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}>
          ‹ Back to Earning Wallet
        </button>

        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 20 }}>Withdraw Funds</h5>

        {/* Balance */}
        <Card style={cardDark} className="p-3 mb-3">
          <div style={{ color: '#888', fontSize: 11, marginBottom: 4 }}>EARNING WALLET BALANCE</div>
          <div>
            <span style={{ fontSize: 28, fontWeight: 800, color: '#66ff99' }}>{balance ?? '—'}</span>
            <span style={{ color: '#aaa', fontSize: 13, marginLeft: 8 }}>USDT</span>
          </div>
        </Card>

        {/* Withdrawal Limit */}
        {limitData && (
          <Card style={{ ...cardDark, border: '1px solid rgba(255,170,68,0.2)', marginBottom: 16 }} className="p-3">
            <div style={{ color: '#ffaa44', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
              Withdrawal Limit
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {[
                { label: 'Total Limit', value: limitData.totalLimit, color: '#ffaa44' },
                { label: 'Used', value: limitData.usedLimit, color: '#ff6666' },
                { label: 'Available', value: limitData.availableLimit, color: '#66ff99' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ textAlign: 'center', background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '8px 6px' }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color }}>{value.toFixed(2)}</div>
                  <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
            {limitData.totalLimit === 0 && (
              <div style={{ color: '#888', fontSize: 11, marginTop: 10, textAlign: 'center' }}>
                Buy slots to unlock your withdrawal limit.
              </div>
            )}
          </Card>
        )}

        {/* Form */}
        <Card style={cardDark} className="p-4 mb-4">
          <form onSubmit={otpStep === 'form' ? handleSendOTP : handleVerifyAndWithdraw}>

            {otpStep === 'form' ? (
              <>
                {/* Amount */}
                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Withdrawal Amount (USDT)</label>
                  <input
                    type="number"
                    min="10"
                    step="any"
                    placeholder="Min. 10 USDT"
                    value={amount}
                    onChange={(e) => { setAmount(e.target.value); setError(''); }}
                    style={{ ...inputStyle, borderColor: isBelowMin || isInsufficient || isOverLimit ? 'rgba(200,50,50,0.5)' : 'rgba(0,229,255,0.2)' }}
                    required
                  />
                  {isBelowMin && <div style={{ color: '#ff6666', fontSize: 11, marginTop: 4 }}>Minimum withdrawal is 10 USDT.</div>}
                  {!isBelowMin && isInsufficient && <div style={{ color: '#ff6666', fontSize: 11, marginTop: 4 }}>Exceeds available balance.</div>}
                  {!isBelowMin && !isInsufficient && isOverLimit && (
                    <div style={{ color: '#ff6666', fontSize: 11, marginTop: 4 }}>
                      Exceeds withdrawal limit. Available limit: {limitData?.availableLimit?.toFixed(2)} USDT.
                    </div>
                  )}

                </div>

                {/* Destination */}
                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Withdraw To</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {[{ val: 'one_wallet', label: 'OneWallet' }, { val: 'external', label: 'Crypto Wallet Address' }].map(({ val, label }) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => { setDestination(val); setError(''); }}
                        style={{
                          flex: 1,
                          padding: '10px 8px',
                          borderRadius: 8,
                          border: destination === val ? `1px solid ${cyan}` : '1px solid rgba(255,255,255,0.1)',
                          background: destination === val ? 'rgba(0,229,255,0.1)' : 'transparent',
                          color: destination === val ? cyan : '#888',
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* External address — pulled from profile */}
                {destination === 'external' && (
                  <div style={{ marginBottom: 18 }}>
                    <label style={labelStyle}>Blockchain Address (USDT BEP-20)</label>
                    {profileUsdtAddress ? (
                      <div style={{
                        background: 'rgba(102,255,153,0.05)',
                        border: '1px solid rgba(102,255,153,0.2)',
                        borderRadius: 8,
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                        flexWrap: 'wrap',
                      }}>
                        <span style={{ color: '#66ff99', fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {profileUsdtAddress}
                        </span>
                      </div>
                    ) : (
                      <div style={{
                        background: 'rgba(255,170,68,0.06)',
                        border: '1px solid rgba(255,170,68,0.25)',
                        borderRadius: 8,
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                        flexWrap: 'wrap',
                      }}>
                        <span style={{ color: '#ffaa44', fontSize: 13 }}>
                          No USDT wallet address saved in your profile.
                        </span>
                        <button
                          type="button"
                          onClick={() => navigate('/profile')}
                          style={{ background: cyan, border: 'none', color: '#000', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Update Profile →
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Charges summary */}
                {numAmount > 0 && destination && (
                  <div style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.12)', borderRadius: 8, padding: '12px 16px', marginBottom: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#888', fontSize: 12 }}>Requested Amount</span>
                      <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{numAmount} USDT</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#888', fontSize: 12 }}>Service Charge ({feePercent}%)</span>
                      <span style={{ color: '#ff6666', fontSize: 12, fontWeight: 600 }}>− {charges} USDT</span>
                    </div>
                    <div style={{ borderTop: '1px solid rgba(0,229,255,0.1)', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>You Receive</span>
                      <span style={{ color: '#66ff99', fontSize: 15, fontWeight: 800 }}>{receivable} USDT</span>
                    </div>
                  </div>
                )}

                {error && <Alert variant="danger" style={{ background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13 }}>{error}</Alert>}

                <button
                  type="submit"
                  disabled={otpSending || isBelowMin || isInsufficient || isOverLimit || !amount || !destination || (destination === 'external' && !profileUsdtAddress)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 8,
                    border: 'none',
                    background: otpSending || isBelowMin || isInsufficient || isOverLimit || !amount || !destination || (destination === 'external' && !profileUsdtAddress) ? 'rgba(255,255,255,0.05)' : cyan,
                    color: otpSending || isBelowMin || isInsufficient || isOverLimit || !amount || !destination || (destination === 'external' && !profileUsdtAddress) ? '#555' : '#000',
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: otpSending ? 'wait' : 'pointer',
                  }}
                >
                  {otpSending ? 'Sending OTP...' : 'Request OTP →'}
                </button>
              </>
            ) : (
              <>
                {/* OTP Verification Step */}
                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Enter OTP</label>
                  <p style={{ fontSize: 12, marginBottom: 12, color: '#888' }}>
                    OTP sent to <span style={{ color: '#00e5ff', fontWeight: 600 }}>{maskEmail(userEmail)}</span> — please check that email account.
                  </p>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                    placeholder="000000"
                    maxLength="6"
                    style={{ ...inputStyle, fontSize: 22, fontWeight: 700, letterSpacing: 8, textAlign: 'center' }}
                    autoFocus
                  />
                  {otpTimer > 0 && (
                    <div style={{ color: '#888', fontSize: 12, marginTop: 8, textAlign: 'center' }}>
                      Resend available in {otpTimer}s
                    </div>
                  )}
                </div>

                {/* Withdrawal summary during OTP step */}
                <div style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.12)', borderRadius: 8, padding: '12px 16px', marginBottom: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#888', fontSize: 12 }}>Requested Amount</span>
                    <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{numAmount} USDT</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#888', fontSize: 12 }}>Service Charge (10%)</span>
                    <span style={{ color: '#ff6666', fontSize: 12, fontWeight: 600 }}>− {charges} USDT</span>
                  </div>
                  <div style={{ borderTop: '1px solid rgba(0,229,255,0.1)', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>You Receive</span>
                    <span style={{ color: '#66ff99', fontSize: 15, fontWeight: 800 }}>{receivable} USDT</span>
                  </div>
                  <div style={{ color: '#888', fontSize: 11, marginTop: 8, textAlign: 'center' }}>
                    To: {destination === 'one_wallet' ? 'OneWallet' : 'External Address'}
                  </div>
                </div>

                {otpError && <Alert variant="danger" style={{ background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13 }}>{otpError}</Alert>}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={!canResendOtp || otpSending}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: 8,
                      border: '1px solid rgba(0,229,255,0.2)',
                      background: canResendOtp ? 'rgba(0,229,255,0.1)' : 'rgba(255,255,255,0.03)',
                      color: canResendOtp ? cyan : '#444',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: canResendOtp ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Resend OTP
                  </button>
                  <button
                    type="submit"
                    disabled={!otp || otp.length !== 6 || submitting}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: 8,
                      border: 'none',
                      background: (!otp || otp.length !== 6 || submitting) ? 'rgba(255,255,255,0.05)' : cyan,
                      color: (!otp || otp.length !== 6 || submitting) ? '#555' : '#000',
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: submitting ? 'wait' : 'pointer',
                    }}
                  >
                    {submitting ? 'Processing...' : 'Confirm & Withdraw'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => { setOtpStep('form'); setOtp(''); setOtpError(''); }}
                  style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', color: '#555', fontSize: 12, cursor: 'pointer' }}
                >
                  ← Back to Form
                </button>
              </>
            )}
          </form>
        </Card>

        {/* History trigger */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 28 }}>
          <button
            type="button"
            onClick={() => setShowHistoryModal(true)}
            style={{ width: '100%', padding: '12px', borderRadius: 8, border: `1px solid rgba(0,229,255,0.3)`, background: 'rgba(0,229,255,0.08)', color: cyan, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            Show Withdrawal History
          </button>
        </div>
      </Container>

      {/* Withdrawal History Modal */}
      {showHistoryModal && (
        <div
          onClick={() => setShowHistoryModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0d0d0d',
              border: '1px solid rgba(0,229,255,0.25)',
              borderRadius: 16,
              padding: '24px',
              width: '100%',
              maxWidth: 720,
              maxHeight: '80vh',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Withdrawal History</div>
              <button onClick={() => setShowHistoryModal(false)} style={{ background: 'none', border: 'none', color: '#888', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {loadingHistory ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" style={{ color: cyan }} /></div>
            ) : history.length === 0 ? (
              <div style={{ color: '#555', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>No withdrawal requests yet.</div>
            ) : (
              <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid rgba(0,229,255,0.12)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                  <thead>
                    <tr style={{ background: 'rgba(0,229,255,0.07)' }}>
                      {['Amount', 'Receivable', 'Destination', 'Date', 'Status', ''].map((h) => (
                        <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '10px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((w) => (
                      <tr key={w._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ color: '#fff', fontWeight: 700, fontSize: 13, padding: '10px 14px', whiteSpace: 'nowrap' }}>{w.amount} USDT</td>
                        <td style={{ color: '#66ff99', fontSize: 13, padding: '10px 14px', whiteSpace: 'nowrap' }}>{w.receivableAmount} USDT</td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ color: w.destination === 'one_wallet' ? '#ffaa44' : cyan, fontSize: 12, fontWeight: 600 }}>
                            {w.destination === 'one_wallet' ? 'OneWallet' : 'External'}
                          </div>
                          {w.externalAddress && (
                            <div style={{ color: '#555', fontSize: 10, fontFamily: 'monospace' }}>{w.externalAddress.slice(0, 12)}...</div>
                          )}
                        </td>
                        <td style={{ color: '#888', fontSize: 11, padding: '10px 14px', whiteSpace: 'nowrap' }}>{fmtIST(w.createdAt)}</td>
                        <td style={{ padding: '10px 14px' }}><StatusBadge status={w.status} /></td>
                        <td style={{ padding: '10px 14px' }}>
                          {w.status === 'pending' && (
                            <button
                              onClick={() => handleCancel(w._id)}
                              disabled={cancellingId === w._id}
                              style={{ background: 'transparent', border: '1px solid rgba(200,50,50,0.4)', color: '#ff6666', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              {cancellingId === w._id ? '...' : 'Cancel'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successData && (() => {
        const isProcessing = successData.status === 'processing';
        const accentColor = isProcessing ? '#66aaff' : '#66ff99';
        return (
        <div
          onClick={() => setSuccessData(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: `radial-gradient(ellipse at 50% 0%, ${isProcessing ? 'rgba(102,170,255,0.08)' : 'rgba(102,255,153,0.08)'} 0%, #0d0d0d 65%)`,
              border: `1px solid ${isProcessing ? 'rgba(102,170,255,0.25)' : 'rgba(102,255,153,0.25)'}`,
              borderRadius: 16,
              padding: '32px 28px',
              width: '100%',
              maxWidth: 380,
              textAlign: 'center',
              animation: 'withdraw-success-pop 0.25s ease-out',
            }}
          >
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: isProcessing ? 'rgba(102,170,255,0.12)' : 'rgba(102,255,153,0.12)',
              border: `1px solid ${isProcessing ? 'rgba(102,170,255,0.4)' : 'rgba(102,255,153,0.4)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 18px',
            }}>
              <span style={{ color: accentColor, fontSize: 30, fontWeight: 700 }}>{isProcessing ? '⏳' : '✓'}</span>
            </div>

            <div style={{ color: '#fff', fontWeight: 800, fontSize: 18, marginBottom: 4 }}>
              {isProcessing ? 'Withdrawal Submitted' : 'Withdrawal Successful'}
            </div>
            <div style={{ color: '#888', fontSize: 13, marginBottom: 22 }}>
              {isProcessing
                ? "Your payout to the external address is being processed. We'll update the status once the network confirms it — if it fails, your earning wallet will be refunded automatically."
                : 'Your funds have been sent to your OneWallet.'}
            </div>

            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '14px 16px', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#888', fontSize: 12 }}>Requested Amount</span>
                <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{successData.amount} USDT</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#888', fontSize: 12 }}>Service Charge ({successData.feePercent}%)</span>
                <span style={{ color: '#ff6666', fontSize: 12, fontWeight: 600 }}>− {successData.charges} USDT</span>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{isProcessing ? "You'll Receive" : 'You Received'}</span>
                <span style={{ color: accentColor, fontSize: 15, fontWeight: 800 }}>{successData.receivableAmount} USDT</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#888', fontSize: 12 }}>Destination</span>
                <span style={{ color: successData.destination === 'one_wallet' ? '#ffaa44' : cyan, fontSize: 12, fontWeight: 600 }}>
                  {successData.destination === 'one_wallet' ? 'OneWallet' : 'External Address'}
                </span>
              </div>
            </div>

            <button
              onClick={() => setSuccessData(null)}
              style={{ width: '100%', marginTop: 22, padding: '12px', borderRadius: 8, border: 'none', background: accentColor, color: '#000', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
            >
              Done
            </button>
          </div>
        </div>
        );
      })()}

      <style>{`
        @keyframes withdraw-success-pop {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default Withdraw;
